import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../../../../components/SearchBar/SearchBar";
import type { SuggestionItem } from "../../../../../components/SearchBar/types";
import { Avatar } from "../../../../../components/Avatar/Avatar";
import { TruncatedTextTooltip } from "../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import { useLazySearchHelpdeskTicketsQuery } from "../../../../../redux/services/helpdeskApi";
import { getHelpdeskLabels } from "../../../../../utils/constants/mappings";
import type { RootState } from "../../../../../redux/store";

const SEARCH_PAGE_LIMIT = 10;

export const TicketSearchBar = () => {
  const navigate = useNavigate();
  const region = useSelector((state: RootState) => state?.auth?.client?.region);
  const helpdeskLabels = getHelpdeskLabels(region);
  const [searchTickets] = useLazySearchHelpdeskTicketsQuery();
  const [hasMoreData, setHasMoreData] = useState(true);
  const [clearSearchValue, setClearSearchValue] = useState(false);

  const handleSearch = useCallback(
    async (query: string, skip: number = 0): Promise<SuggestionItem[]> => {
      if (!query?.trim()) return [];
      const page = Math.floor(skip / SEARCH_PAGE_LIMIT) + 1;
      try {
        const response = await searchTickets({
          search: query,
          page,
          limit: SEARCH_PAGE_LIMIT,
        }).unwrap();

        const tickets = Array.isArray(response)
          ? response
          : (response?.tickets ?? response?.data ?? []);

        const total = response?.total ?? response?.total_count;
        if (total != null) {
          const totalPages = Math.ceil(total / SEARCH_PAGE_LIMIT);
          setHasMoreData(page < totalPages);
        } else {
          setHasMoreData(tickets?.length >= SEARCH_PAGE_LIMIT);
        }

        return tickets?.map(
          (ticket: { ticket_id: number; title?: string }): SuggestionItem => {
            const title = ticket.title || "Untitled";
            return {
              id: ticket.ticket_id,
              value: String(ticket.ticket_id),
              content: (
                <div className="p-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-[8px] min-w-0">
                    <Avatar text={title} size={24} />
                    <TruncatedTextTooltip
                      text={title}
                      maxWidth="max-w-[180px]"
                      className="text-data dark:text-data-dk font-medium text-md"
                    />
                  </div>
                  <span className="text-sm font-medium text-secondary-text dark:text-secondary-text-dk shrink-0">
                    #{ticket.ticket_id}
                  </span>
                </div>
              ),
            };
          }
        );
      } catch {
        return [];
      }
    },
    [searchTickets]
  );

  const handleSelect = useCallback(
    (_value: string, item?: SuggestionItem) => {
      if (item?.id) {
        navigate(`/dashboard/crm/helpdesk/all-tickets/${item.id}`);
        setClearSearchValue(true);
      }
    },
    [navigate]
  );

  return (
    <div className="w-[280px] mx-auto">
      <SearchBar
        id="helpdesk-search"
        placeholder={helpdeskLabels.searchPlaceholder}
        noSuggestionsText={helpdeskLabels.noSearchResults}
        enableApiSearch={true}
        enableInfiniteScroll={true}
        hasMoreDataToLoad={hasMoreData}
        onApiSearch={handleSearch}
        onSelect={handleSelect}
        clearSearchValue={clearSearchValue}
        setClearSearchValue={setClearSearchValue}
        apiSearchDebounceMs={1000}
        minCharsToSearch={1}
        suggestionsClassName="max-h-[300px]"
        containerClassName="w-[280px]"
        className="pl-[8px] pr-[8px] pt-[7px] pb-[7px] hover:border-b-2 hover:border-b-[#1C6CFF] hover:shadow-[2px_4px_20px_rgba(0,0,0,0.1)] hover:rounded-[6px]"
        suggestionItemClassName="!p-0"
      />
    </div>
  );
};
